import React, {useCallback, useRef} from "react";
import {Button, Grid, IconButton, Typography} from "@mui/material";
import {AttachmentOutlined, DeleteOutlined} from "@mui/icons-material";
import {useDropzone} from "react-dropzone";

export interface IFileData {
    nome: string;
    prefixo: string;
    tipo: 'jpg' | 'jpeg' | 'png' | 'pdf';
    conteudoBase64: string;
    extensao: string;
    file?: File;
}

type fileType = 'imagem' | 'csv'

export interface FileInputEntryProps {
    multiple?: true
    files?: IFileData[]
    fileType?: fileType
    onChange?: (files?: IFileData[]) => void;
    slotProps?: {
        input?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;
    }
}

export function FileInputEntry(props: FileInputEntryProps) {
    const {files, onChange, multiple, slotProps} = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const onDrop = useCallback((acceptedFiles: any) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const fileData: IFileData = {
                nome: acceptedFiles[0].name,
                prefixo: acceptedFiles[0].name.split('.').slice(0, -1).join('.'),
                tipo: acceptedFiles[0].name.split('.').pop()?.toLowerCase() as 'jpg' | 'jpeg' | 'png' | 'pdf',
                conteudoBase64: event.target?.result as string,
                extensao: acceptedFiles[0].name.split('.').pop() as string,
                file: acceptedFiles[0]
            };
            if (onChange != null) {
                onChange([fileData]);
            }
        }
        reader.readAsDataURL(acceptedFiles[0]);

    }, []);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop, multiple: false});


    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        const filesArray = Array.from(selectedFiles);

        const filePromises = filesArray.map(file => {
            return new Promise<IFileData>((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const fileData: IFileData = {
                        nome: file.name,
                        prefixo: file.name.split('.').slice(0, -1).join('.'),
                        tipo: file.name.split('.').pop()?.toLowerCase() as 'jpg' | 'jpeg' | 'png' | 'pdf',
                        conteudoBase64: event.target?.result as string,
                        extensao: file.name.split('.').pop() as string,
                        file: file
                    };
                    resolve(fileData);
                };
                reader.readAsDataURL(file);
            });
        });

        Promise.all(filePromises).then(readFiles => {
            if (onChange) {
                if (multiple) {
                    onChange([...(files || []), ...readFiles]);
                } else {
                    onChange([readFiles[0]]);
                }
            }
        });

        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleUploadClick = () => {
        inputRef.current?.click();
    };

    const handleClearAll = () => {
        if (onChange) {
            onChange(multiple ? [] : undefined);
        }
    }

    const fileNames = files && files.length > 0 ? files.map(f => f.nome).join(', ') : 'Nenhum arquivo selecionado';


    return (
        <Grid {...getRootProps()}
              container
              justifyContent={'space-between'}
              alignItems={'center'}
              spacing={2}>
            <Grid>
                <input
                    {...getInputProps()}
                    type={'file'}
                    multiple={multiple}
                    accept={props.fileType == 'csv' ? 'text/csv' : 'image/*,application/pdf'}
                    {...slotProps?.input}
                    onChange={changeHandler}
                    ref={inputRef}
                    style={{display: 'none'}}
                />
                <Button variant="contained"
                        color="info" onClick={handleUploadClick}
                        endIcon={<AttachmentOutlined sx={{transform: 'rotate(115deg)'}}/>}>
                    {isDragActive ? "Largar arquivo" : "Anexar Arquivo"}
                </Button>
            </Grid>
            <Grid>
                <Typography variant="body2" color="textSecondary">
                    {fileNames}
                </Typography>
            </Grid>
            <Grid>
                <IconButton onClick={handleClearAll} disabled={!files || files.length === 0}>
                    <DeleteOutlined/>
                </IconButton>
            </Grid>
        </Grid>
    )
}